import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface PlanCapabilities {
  name: string;
  display_name: string;
  search_limit_daily: number;
  export_enabled: boolean;
  collections_enabled: boolean;
  shared_collections: boolean;
  audit_access: boolean;
  custom_limits: boolean;
  max_team_users: number;
}

const FREE_FALLBACK: PlanCapabilities = {
  name: "free",
  display_name: "Free",
  search_limit_daily: 1,
  export_enabled: false,
  collections_enabled: false,
  shared_collections: false,
  audit_access: false,
  custom_limits: false,
  max_team_users: 1,
};

export function usePlan() {
  const { user, profile, isAdmin } = useAuth();
  const [plan, setPlan] = useState<PlanCapabilities | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!user || !profile?.plan_id) {
        if (!cancelled) {
          setPlan(FREE_FALLBACK);
          setLoading(false);
        }
        return;
      }
      const { data } = await supabase
        .from("plans")
        .select("name, display_name, search_limit_daily, export_enabled, collections_enabled, shared_collections, audit_access, custom_limits, max_team_users")
        .eq("id", profile.plan_id)
        .maybeSingle();
      if (!cancelled) {
        setPlan((data as PlanCapabilities) ?? FREE_FALLBACK);
        setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [user, profile?.plan_id]);

  // Admins get unlimited / all features regardless of plan row
  const isFreeTier = !isAdmin && (plan?.name === "free" || !plan);

  return { plan, loading, isFreeTier, isAdmin };
}
